import React, { useState, useRef } from 'react';
import { toPng } from 'html-to-image';
import GardenExportView from './GardenExportView';
import '../../../css/gardenManagerStylings/GardenExportPopupStyling.css';

const GardenExportPopup = ({ garden, isMobile, onClose }) => {
    const [exportConfig, setExportConfig] = useState({
        title: garden?.name || "My Garden Plan",
        subtext: "My Garden Plan",
        showTitle: true,
        showSubtext: true,
        showFooter: true,
        showWatermark: true,
        showBackground: true,
        showGradientBackground: true,
        logoPosition: 'bottom-right',
        titleAlignment: 'left',
        subtextAlignment: 'right'
    });

    const [isExporting, setIsExporting] = useState(false);
    const exportViewRef = useRef();

    const handleConfigChange = (key, value) => {
        setExportConfig(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleExport = async () => {
        if (!exportViewRef.current) return;

        setIsExporting(true);
        try {
            const node = exportViewRef.current;
            const rect = node.getBoundingClientRect();

            const dataUrl = await toPng(node, {
                width: rect.width,
                height: rect.height,
                pixelRatio: 2,
            });

            const link = document.createElement("a");
            link.download = `${exportConfig.title.replace(/\s+/g, '-').toLowerCase()}.png`;
            link.href = dataUrl;
            link.click();

            // Close popup after successful export
            setTimeout(() => {
                onClose();
            }, 500);
        } catch (err) {
            console.error("Export failed", err);
            alert("Failed to export garden. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="garden-export-popup-overlay" onClick={onClose}>
            <div className="garden-export-popup" onClick={(e) => e.stopPropagation()}>
                <div className="export-popup-header">
                    <h2>Export Garden Plan</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                <div className="export-popup-body">
                    {/* Configuration Panel */}
                    <div className="export-config-panel">
                        <h3>Customize Export</h3>

                        {/* Title Input */}
                        <div className="config-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={exportConfig.showTitle}
                                    onChange={(e) => handleConfigChange('showTitle', e.target.checked)}
                                />
                                Show Title
                            </label>
                            {exportConfig.showTitle && (
                                <input
                                    type="text"
                                    className="export-garden-title-input"
                                    value={exportConfig.title}
                                    onChange={(e) => handleConfigChange('title', e.target.value)}
                                    placeholder="Enter garden title"
                                    maxLength={50}
                                />
                            )}
                        </div>

                        {/* Title Alignment */}
                        {exportConfig.showTitle && (
                            <div className="config-group">
                                <label>Title Alignment</label>
                                <div className="button-group">
                                    {['left', 'center', 'right'].map(align => (
                                        <button
                                            key={align}
                                            className={`option-button ${exportConfig.titleAlignment === align ? 'active' : ''}`}
                                            onClick={() => handleConfigChange('titleAlignment', align)}
                                        >
                                            {align.charAt(0).toUpperCase() + align.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Subtext Input */}
                        <div className="config-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={exportConfig.showSubtext}
                                    onChange={(e) => handleConfigChange('showSubtext', e.target.checked)}
                                />
                                Show Subtext
                            </label>
                            {exportConfig.showSubtext && (
                                <input
                                    type="text"
                                    className="export-garden-title-input"
                                    value={exportConfig.subtext}
                                    onChange={(e) => handleConfigChange('subtext', e.target.value)}
                                    placeholder="Enter garden subtext"
                                    maxLength={50}
                                />
                            )}
                        </div>

                        {/* Subtext Alignment */}
                        {exportConfig.showSubtext && (
                            <div className="config-group">
                                <label>Subtext Alignment</label>
                                <div className="button-group">
                                    {['left', 'center', 'right'].map(align => (
                                        <button
                                            key={align}
                                            className={`option-button ${exportConfig.subtextAlignment === align ? 'active' : ''}`}
                                            onClick={() => handleConfigChange('subtextAlignment', align)}
                                        >
                                            {align.charAt(0).toUpperCase() + align.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Footer Toggle */}
                        <div className="config-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={exportConfig.showFooter}
                                    onChange={(e) => handleConfigChange('showFooter', e.target.checked)}
                                />
                                Show Footer Text
                            </label>
                        </div>

                        {/* Watermark Toggle */}
                        <div className="config-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={exportConfig.showWatermark}
                                    onChange={(e) => handleConfigChange('showWatermark', e.target.checked)}
                                />
                                Show Watermark
                            </label>
                        </div>

                        {/* Logo Position */}
                        {exportConfig.showWatermark && (
                            <div className="config-group">
                                <label>Watermark Position</label>
                                <div className="button-group-watermark">
                                    {[
                                        {value: 'top-left', label: 'Top Left'},
                                        {value: 'top-right', label: 'Top Right'},
                                        {value: 'bottom-left', label: 'Bottom Left'},
                                        {value: 'bottom-right', label: 'Bottom Right'}
                                    ].map(pos => (
                                        <button
                                            key={pos.value}
                                            className={`option-button ${exportConfig.logoPosition === pos.value ? 'active' : ''}`}
                                            onClick={() => handleConfigChange('logoPosition', pos.value)}
                                        >
                                            {pos.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}


                        {/* Garden Background Toggle */}
                        <div className="config-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={exportConfig.showBackground}
                                    onChange={(e) => handleConfigChange('showBackground', e.target.checked)}
                                />
                                Show Garden Background
                            </label>
                        </div>

                        {/* Gradient Background Toggle */}
                        <div className="config-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={exportConfig.showGradientBackground}
                                    onChange={(e) => handleConfigChange('showGradientBackground', e.target.checked)}
                                />
                                Show Image Background
                            </label>
                        </div>
                    </div>

                    {/* Preview Panel */}
                    <div className="export-preview-panel">
                        <h3>Preview</h3>
                        <div className="preview-container">
                            <div className="preview-wrapper">
                                <GardenExportView
                                    ref={exportViewRef}
                                    garden={garden}
                                    isMobile={isMobile}
                                    config={exportConfig}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Export Button */}
                <div className="export-popup-footer">
                    <button className="cancel-button" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="export-button"
                        onClick={handleExport}
                        disabled={isExporting}
                    >
                        {isExporting ? 'Exporting...' : 'Download Image'}
                    </button>
                </div>

                {/* Hidden export view for actual export */}
                <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
                    <GardenExportView
                        ref={exportViewRef}
                        garden={garden}
                        isMobile={isMobile}
                        config={exportConfig}
                    />
                </div>
            </div>
        </div>
    );
};

export default GardenExportPopup;